import { Tab, Tabs } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { ConfigContext } from 'components/ConfigGuard';
import EditProfilePageView from 'components/profile/EditProfilePageView';
import Spinner from 'components/shared/Spinner';
import { UserContext } from 'components/UserGuard';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import SiteModule from 'store/enums/SiteModule';
import UserRelationshipPageView from 'components/profile/UserRelationshipPageView';
import EditPreferencesPage from 'components/profile/EditPreferencesPage';
import AddressesPage from 'components/addresses/AddressesPage';
import { ParametersContext } from 'components/ParametersGuard';
import UserEmployerPageView from 'components/profile/UserEmployerPageView';
import AdvocacyPage from 'components/profile/AdvocacyPage';
import useRequest from 'hooks/useRequest';
import { CompanyRequestsInfo } from 'store/types/Company';
import UserService from 'services/api/UserService';
import CountBadge from 'components/shared/CountBadge';
import { hasMemberType } from 'util/User';
import routes from 'store/configs/Routes';
import classNames from 'classnames';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';
import { TabItem } from 'store/types/Tabs';

import commonStyles from 'styles/common.module.scss';
import styles from './ProfilePage.module.scss';

const tabConfig = {
  Profile: { key: 'Profile', label: 'Profile Information', route: routes.profile } as TabItem,
  ProfilePreferences: {
    key: 'ProfilePreferences',
    label: 'Additional Information',
    route: routes.profilePreferences,
  } as TabItem,
  ProfileAddresses: {
    key: 'ProfileAddresses',
    label: 'Manage My Addresses',
    route: routes.profileAddresses,
  } as TabItem,
  ProfileEmployer: { key: 'ProfileEmployer', label: 'My Employer', route: routes.profileEmployer } as TabItem,
  ProfileRelationships: {
    key: 'ProfileRelationships',
    label: 'My Friends & Family',
    route: routes.profileRelationships,
  } as TabItem,
  Advocacy: { key: 'Advocacy', label: 'Advocacy', route: routes.advocacy } as TabItem,
};

const ProfilePage: React.FunctionComponent = () => {
  const location = useLocation();
  const history = useHistory();
  const {
    enabledModules,
    isNccerTheme,
    isBayViewTheme,
    modulesSettings: {
      profile: {
        enableRelationship,
        enableMyEmployer,
        enablePreferences,
        enableAdvocacy,
        relationship,
        myEmployer,
        preferences,
        advocacy,
        hiddenTabs,
      },
    },
  } = useContext(ConfigContext);
  const { memberTypeName, userLoading = false } = useContext(UserContext);
  const {
    profile: {
      profileInfoHeader,
      additionalInfoHeader,
      relationshipHeader,
      manageMyAddressHeader,
      myEmployerHeader,
      advocacyHeader,
    },
  } = useContext(ParametersContext);
  const { data, refetch } = useRequest<CompanyRequestsInfo>(UserService.getEmployerRequestsCount);
  const activeTab: string = useMemo(() => getTabKeyByRoute(location.pathname, tabConfig), [location.pathname]);

  const profileRelationshipsTabLabel = useMemo(
    () => (isBayViewTheme ? 'Friends & Guests' : tabConfig.ProfileRelationships.label),
    [isBayViewTheme]
  );

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const manageAddressesModuleEnabled: boolean = useMemo(
    () => enabledModules.includes(SiteModule.Addresses),
    [enabledModules]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, location.pathname, visibleTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.Profile.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  return (
    <TabContext value={activeTab}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor={'primary'}
        textColor={'primary'}
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={{
          '& .MuiTabs-flexContainer': {
            flexWrap: 'wrap',
          },
        }}
      >
        {visibleTabs.map((tab) => {
          if (
            tab.key === tabConfig.ProfilePreferences.key &&
            (!enablePreferences ||
              preferences?.enable === false ||
              !hasMemberType(memberTypeName, preferences?.memberTypes))
          ) {
            return;
          }

          if (tab.key === tabConfig.ProfileAddresses.key && (isNccerTheme || !manageAddressesModuleEnabled)) {
            return;
          }

          if (
            tab.key === tabConfig.ProfileEmployer.key &&
            (!enableMyEmployer ||
              myEmployer?.enable === false ||
              !hasMemberType(memberTypeName, myEmployer?.memberTypes))
          ) {
            return;
          }

          if (
            tab.key === tabConfig.ProfileRelationships.key &&
            (!enableRelationship ||
              relationship?.enable === false ||
              !hasMemberType(memberTypeName, relationship?.memberTypes))
          ) {
            return;
          }

          if (
            tab.key === tabConfig.Advocacy.key &&
            (!enableAdvocacy || advocacy?.enable === false || !hasMemberType(memberTypeName, advocacy?.memberTypes))
          ) {
            return;
          }

          if (
            tab.key === tabConfig.ProfileEmployer.key &&
            (enableMyEmployer || myEmployer?.enable === true) &&
            hasMemberType(memberTypeName, myEmployer?.memberTypes)
          ) {
            return (
              <Tab
                key={tabConfig.ProfileEmployer.key}
                value={tabConfig.ProfileEmployer.key}
                label={
                  <>
                    {tabConfig.ProfileEmployer.label}
                    {!!data?.pending && <CountBadge className={styles.countBadge}>{data.pending}</CountBadge>}
                  </>
                }
                className={classNames(commonStyles.tab, styles.tabWithCounter)}
                classes={{ selected: commonStyles.activeTab }}
              />
            );
          }

          return (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.key === tabConfig.ProfileRelationships.key ? profileRelationshipsTabLabel : tab.label}
              className={commonStyles.tab}
              classes={{ selected: commonStyles.activeTab }}
            />
          );
        })}
      </Tabs>
      <TabPanel value={tabConfig.Profile.key} className={commonStyles.tabPanel}>
        <Spinner loading={userLoading} transparent={false}>
          {profileInfoHeader && <p dangerouslySetInnerHTML={{ __html: profileInfoHeader }} />}
          <EditProfilePageView />
        </Spinner>
      </TabPanel>
      {(enablePreferences || preferences?.enable === true) && hasMemberType(memberTypeName, preferences?.memberTypes) && (
        <TabPanel value={tabConfig.ProfilePreferences.key} className={commonStyles.tabPanel}>
          <Spinner loading={userLoading} transparent={false}>
            {additionalInfoHeader && <p dangerouslySetInnerHTML={{ __html: additionalInfoHeader }} />}
            <EditPreferencesPage />
          </Spinner>
        </TabPanel>
      )}
      {!isNccerTheme && manageAddressesModuleEnabled && (
        <TabPanel value={tabConfig.ProfileAddresses.key} className={commonStyles.tabPanel}>
          {manageMyAddressHeader && <p dangerouslySetInnerHTML={{ __html: manageMyAddressHeader }} />}
          <AddressesPage />
        </TabPanel>
      )}
      {(enableMyEmployer || myEmployer?.enable === true) && hasMemberType(memberTypeName, myEmployer?.memberTypes) && (
        <TabPanel value={tabConfig.ProfileEmployer.key} className={commonStyles.tabPanel}>
          {myEmployerHeader && <p dangerouslySetInnerHTML={{ __html: myEmployerHeader }} />}
          <UserEmployerPageView refetchCount={refetch} />
        </TabPanel>
      )}
      {(enableRelationship || relationship?.enable === true) &&
        hasMemberType(memberTypeName, relationship?.memberTypes) && (
          <TabPanel value={tabConfig.ProfileRelationships.key} className={commonStyles.tabPanel}>
            {relationshipHeader && <p dangerouslySetInnerHTML={{ __html: relationshipHeader }} />}
            <UserRelationshipPageView />
          </TabPanel>
        )}
      {(enableAdvocacy || advocacy?.enable === true) && hasMemberType(memberTypeName, advocacy?.memberTypes) && (
        <TabPanel value={tabConfig.Advocacy.key} className={commonStyles.tabPanel}>
          {advocacyHeader && <p dangerouslySetInnerHTML={{ __html: advocacyHeader }} />}
          <AdvocacyPage />
        </TabPanel>
      )}
    </TabContext>
  );
};
export default ProfilePage;
